import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { authUpdateMe, formatApiErrorDetail } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useI18n } from "../lib/i18n";
import { useNavigate } from "react-router-dom";
import { User, Mail, Calendar, Shield, LogOut, Save } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
    const { user, logout, refresh } = useAuth();
    const nav = useNavigate();
    const { currencies, setCurrency } = useCurrency();
    const { languages, setLang } = useI18n();
    const [name, setName] = useState(user?.name || "");
    const [prefCur, setPrefCur] = useState(user?.preferred_currency || "USD");
    const [prefLang, setPrefLang] = useState(user?.preferred_language || "it");
    const [busy, setBusy] = useState(false);

    if (!user) return null;

    const save = async () => {
        setBusy(true);
        try {
            await authUpdateMe({
                name: name.trim(),
                preferred_currency: prefCur,
                preferred_language: prefLang,
            });
            setCurrency(prefCur);
            setLang(prefLang);
            await refresh();
            toast.success("Profilo aggiornato");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const onLogout = async () => {
        await logout();
        toast.success("Disconnesso");
        nav("/login", { replace: true });
    };

    return (
        <div data-testid="profile-page" className="px-4 py-6 max-w-2xl mx-auto fade-up">
            <div className="mb-6">
                <div className="text-[10px] text-mute tracking-[0.2em] mb-1">ACCOUNT</div>
                <h1 className="font-display text-4xl font-bold tracking-tight brand-gradient-text">Il tuo profilo</h1>
            </div>

            <div className="border border-soft bg-elev p-5 mb-4">
                <div className="flex items-center gap-4 mb-5">
                    {user.picture ? (
                        <img src={user.picture} alt="" width={64} height={64} className="rounded-full border border-soft" />
                    ) : (
                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold"
                            style={{ background: "linear-gradient(135deg, var(--brand-blue), var(--brand-green))", color: "#000" }}>
                            {(user.name || user.email || "U")[0].toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div className="text-lg font-semibold" data-testid="profile-name">{user.name}</div>
                        <div className="text-mute text-xs" data-testid="profile-email">{user.email}</div>
                        <div className="flex gap-2 mt-1 text-[10px]">
                            <span className="px-1.5 py-0.5 border" style={{ borderColor: "var(--amber)", color: "var(--amber)" }}>
                                {user.auth_provider === "google" ? "GOOGLE" : "EMAIL"}
                            </span>
                            {user.role === "admin" && (
                                <span className="px-1.5 py-0.5 border flex items-center gap-1" style={{ borderColor: "var(--brand-green)", color: "var(--brand-green)" }}>
                                    <Shield size={10} /> ADMIN
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <Row label="Nome" icon={User}>
                        <input
                            data-testid="profile-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                        />
                    </Row>
                    <Row label="Email" icon={Mail}>
                        <div className="text-sm text-mute tnum">{user.email}</div>
                    </Row>
                    <Row label="Valuta preferita">
                        <select
                            data-testid="profile-currency"
                            value={prefCur}
                            onChange={(e) => setPrefCur(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                            style={{ colorScheme: "dark" }}
                        >
                            {Object.entries(currencies).map(([code, info]) => (
                                <option key={code} value={code}>{info.symbol} {code} — {info.name}</option>
                            ))}
                        </select>
                    </Row>
                    <Row label="Lingua preferita">
                        <select
                            data-testid="profile-language"
                            value={prefLang}
                            onChange={(e) => setPrefLang(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                            style={{ colorScheme: "dark" }}
                        >
                            {Object.values(languages).map(l => (
                                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                            ))}
                        </select>
                    </Row>
                    {user.created_at && (
                        <Row label="Membro dal" icon={Calendar}>
                            <div className="text-sm text-mute tnum">{user.created_at.slice(0, 10)}</div>
                        </Row>
                    )}
                </div>

                <div className="flex gap-2 mt-5">
                    <button
                        data-testid="profile-save"
                        onClick={save}
                        disabled={busy}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-50"
                        style={{ background: "var(--brand-blue)", color: "#000" }}
                    >
                        <Save size={14} /> {busy ? "Salvataggio…" : "Salva"}
                    </button>
                    <button
                        data-testid="profile-logout"
                        onClick={onLogout}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border"
                        style={{ borderColor: "var(--red)", color: "var(--red)" }}
                    >
                        <LogOut size={14} /> Esci
                    </button>
                </div>
            </div>
        </div>
    );
}

const Row = ({ label, icon: Icon, children }) => (
    <div>
        <label className="text-[10px] text-mute tracking-widest block mb-1 flex items-center gap-1.5">
            {Icon && <Icon size={11} />}
            {label}
        </label>
        {children}
    </div>
);
